/**
 * Table PDF Component - Converted to use @react-pdf/renderer
 *
 * This component has been converted from the old Canvas-based PDF generation
 * to use the new @react-pdf/renderer library, following the same pattern as
 * pdf.component.tsx and user-service-orders-pdf.tsx.
 *
 * Changes made:
 * - Replaced jsPDF + Canvas with @react-pdf/renderer
 * - Created TablePDFDocument component using Document/Page/View/Text
 * - Replaced Canvas drawing functions with react-pdf styling
 * - Used PDFDownloadLink instead of manual PDF generation
 * - Commented out old Canvas-based implementation for future reference
 *
 * Note: @react-pdf/renderer package needs to be installed
 * Run: npm install @react-pdf/renderer
 */

// Old Canvas-based PDF implementation commented for future reference
// import { LoadingButton } from "@mui/lab";
// import jsPDF from "jspdf";
// import { useState } from "react";
// import { formatCurrency } from "../../../shared/lib/numbers";
// import { drawBorder } from "../../../utils/helpers/pdf";
// import { removeWhiteSpaces } from "../../../utils/helpers/strings";
// import Canvas from "./canvas.component";
// import { CarPart } from "../../../entities/pericia";

// New react-pdf based implementation
import React from "react";
import { Document, Page, Text, View, StyleSheet, Font, Image } from "@react-pdf/renderer";
import { Button } from "@mui/material";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { Car } from "../../../entities/car/model/car";
import { formatCurrency } from "../../../shared/lib/numbers";
import { removeWhiteSpaces } from "../../../utils/helpers/strings";
import { CarPart } from "../../../entities/pericia";

const nuovautoIMG = require("../../../assets/nuovauto.png");

Font.register({
  family: "Helvetica",
  src: "https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.53/fonts/Helvetica.ttf"
});

interface ListPDFProps {
  carParts: CarPart[];
  car: Car;
  aditionalCost: number;
  aditionalCostDescription: string;
  unmountPrice: number;
  costumer: string;
  date: Date;
  pricePerWorkingHour: number;
  insuranceHours: number;
  costumerPrice: number;
}

// New PDF styles using react-pdf
const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 12,
    padding: 15,
    backgroundColor: "#ffffff",
    color: "#000",
    lineHeight: 1.4
  },
  pageContent: {
    borderWidth: 2,
    borderColor: "#000",
    borderStyle: "solid",
    padding: 15,
    minHeight: "95%"
  },
  header: {
    marginBottom: 15,
    alignItems: "flex-start"
  },
  logo: {
    width: 180,
    height: 60,
    alignSelf: "center",
    marginBottom: 20
  },
  customerInfo: {
    marginBottom: 20,
    fontSize: 10
  },
  flexRow: {
    flexDirection: "row",
    marginBottom: 8
  },
  customerCol: {
    width: "33%",
    textAlign: "left"
  },
  boldLabel: {
    fontWeight: "bold"
  },
  table: {
    marginTop: 20
  },
  tableHeader: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#000",
    paddingBottom: 5,
    marginBottom: 5,
    fontSize: 10,
    fontWeight: "bold"
  },
  tableHeaderRow: {
    flexDirection: "row",
    marginBottom: 5,
    fontSize: 10,
    fontWeight: "bold"
  },
  headerCell: {
    backgroundColor: "#4CAF50",
    color: "#ffffff",
    padding: 8,
    margin: 1,
    textAlign: "center",
    borderRadius: 3,
    flexGrow: 1,
    maxWidth: "32%"
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderColor: "#ccc",
    paddingVertical: 5,
    fontSize: 9
  },
  col1: { width: "15%", textAlign: "left" }, // Desc
  col2: { width: "8%", textAlign: "left" }, // 25mm
  col3: { width: "8%", textAlign: "left" }, // AW
  col4: { width: "8%", textAlign: "left" }, // >25mm
  col5: { width: "8%", textAlign: "left" }, // AW
  col6: { width: "8%", textAlign: "left" }, // Cola
  col7: { width: "8%", textAlign: "left" }, // Alum
  col8: { width: "10%", textAlign: "left" }, // Pintura
  col9: { width: "10%", textAlign: "left" }, // Total
  col10: { width: "10%", textAlign: "left" }, // CHF/Ora
  col11: { width: "12%", textAlign: "left" }, // Subtotale
  bottomContainer: {
    flexDirection: "row",
    marginTop: 20,
    justifyContent: "space-between",
    alignItems: "flex-start"
  },
  messagesContainer: {
    width: "45%",
    alignItems: "flex-start"
  },
  pricesContainer: {
    width: "45%",
    alignItems: "flex-end"
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 5,
    width: 200
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderColor: "#000",
    paddingTop: 5,
    width: 200,
    fontWeight: "bold"
  },
  noteSection: {
    marginBottom: 10,
    alignItems: "flex-start"
  },
  noteText: {
    fontSize: 9,
    fontStyle: "italic",
    color: "#666"
  }
});

// PDF Document Component
const TablePDFDocument: React.FC<ListPDFProps> = ({
  carParts,
  costumer,
  pricePerWorkingHour,
  unmountPrice,
  car,
  aditionalCost,
  aditionalCostDescription,
  date,
  insuranceHours = 0,
  costumerPrice = 0
}) => {
  const { chassisNumber } = car;
  const totalHours = carParts.reduce((acc, curr) => acc + curr.workingHours, 0);
  const totalPrice = carParts.reduce((acc, curr) => acc + curr.price, 0);

  const workingHours = totalHours === 0 && insuranceHours > 0 ? insuranceHours : totalHours / 10;
  let customTotalPrice = totalPrice;

  if (insuranceHours > 0) {
    customTotalPrice = insuranceHours * pricePerWorkingHour;
  }

  if (costumerPrice > 0) {
    customTotalPrice = costumerPrice;
  }

  let finalTotal = customTotalPrice;
  if (unmountPrice > 0) finalTotal += unmountPrice;
  if (aditionalCost > 0) finalTotal += aditionalCost;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.pageContent}>
          {/* Header with Logo */}
          <View style={styles.header}>
            <Image src={nuovautoIMG} style={styles.logo} />
          </View>

          {/* Customer Information */}
          <View style={styles.customerInfo}>
            <View style={styles.flexRow}>
              <Text style={styles.customerCol}>
                <Text style={styles.boldLabel}>Cliente:</Text> {costumer}
              </Text>
              <Text style={styles.customerCol}>
                <Text style={styles.boldLabel}>Marca:</Text> {car.brand}
              </Text>
              <Text style={styles.customerCol}>
                <Text style={styles.boldLabel}>Modello:</Text> {car.model}
              </Text>
            </View>
            <View style={styles.flexRow}>
              <Text style={styles.customerCol}>
                <Text style={styles.boldLabel}>Targa:</Text> {car.plate}
              </Text>
              <Text style={styles.customerCol}>
                <Text style={styles.boldLabel}>Telaio:</Text> {car.chassisNumber}
              </Text>
              <Text style={styles.customerCol}>
                <Text style={styles.boldLabel}>Assicurazione:</Text> {car.insuranceName}
              </Text>
            </View>
            <View style={styles.flexRow}>
              <Text style={styles.customerCol}>
                <Text style={styles.boldLabel}>Colore:</Text> {car.color}
              </Text>
              <Text style={styles.customerCol}>
                <Text style={styles.boldLabel}>Data:</Text> {date.toLocaleDateString("pt-br")}
              </Text>
              <Text style={styles.customerCol}></Text>
            </View>
          </View>

          {/* Table */}
          <View style={styles.table}>
            {/* Table Header */}
            <View style={styles.tableHeader}>
              <Text style={styles.col1}>Desc</Text>
              <Text style={styles.col2}>25mm</Text>
              <Text style={styles.col3}>AW</Text>
              <Text style={styles.col4}>&gt;25mm</Text>
              <Text style={styles.col5}>AW</Text>
              <Text style={styles.col6}>Cola</Text>
              <Text style={styles.col7}>Alum</Text>
              <Text style={styles.col8}>Verniciare</Text>
              <Text style={styles.col9}>Total</Text>
              <Text style={styles.col10}>CHF/Ora</Text>
              <Text style={styles.col11}>Subtotale</Text>
            </View>

            {/* Table Rows */}
            {carParts.map((carPart, index) => (
              <View key={index} style={styles.tableRow}>
                <Text style={styles.col1}>{carPart.name}</Text>
                <Text style={styles.col2}>{carPart.smallSmash}</Text>
                <Text style={styles.col3}>{carPart.smallSmashWorkingHours}</Text>
                <Text style={styles.col4}>{carPart.smash}</Text>
                <Text style={styles.col5}>{carPart.smashWorkingHours}</Text>
                <Text style={styles.col6}>{carPart.shouldGlue ? "X" : ""}</Text>
                <Text style={styles.col7}>{carPart.isAluminum ? "X" : ""}</Text>
                <Text style={styles.col8}>{carPart.shouldPaint ? "X" : ""}</Text>
                <Text style={styles.col9}>{carPart.workingHours.toFixed(2)}</Text>
                <Text style={styles.col10}>{pricePerWorkingHour.toFixed(2)}</Text>
                <Text style={styles.col11}>{formatCurrency(carPart.price)}</Text>
              </View>
            ))}
          </View>

          {/* Bottom Container with Messages and Prices */}
          <View style={styles.bottomContainer}>
            {/* Messages Container (Left) */}
            <View style={styles.messagesContainer}>
              {insuranceHours > 0 && costumerPrice === 0 && (
                <View style={styles.noteSection}>
                  <Text style={styles.noteText}>
                    <Text style={{ fontWeight: "bold" }}>*Nota: </Text>
                    Questa perizia sta utilizzando le ore stimate dall'assicurazione. Ore assicurazione: {insuranceHours.toFixed(2)}h.                  </Text>
                </View>
              )}
              {costumerPrice > 0 && (
                <View style={styles.noteSection}>
                  <Text style={styles.noteText}>
                    <Text style={{ fontWeight: "bold" }}>*Nota: </Text>
                    Questa perizia sta utilizzando il prezzo negoziato con il cliente. Prezzo pattuito: {formatCurrency(costumerPrice)}.
                  </Text>
                </View>
              )}
              {aditionalCost > 0 && aditionalCostDescription && (
                <View style={styles.noteSection}>
                  <Text style={styles.noteText}>
                    <Text style={{ fontWeight: "bold" }}>*Botta Aggiuntiva: </Text>
                    {aditionalCostDescription}
                  </Text>
                </View>
              )}
              {/* Future messages can be added here */}
            </View>

            {/* Prices Container (Right) */}
            <View style={styles.pricesContainer}>
              <View style={styles.summaryRow}>
                <Text>Ore{insuranceHours > 0 ? "*" : ""}:</Text>
                <Text>{workingHours.toFixed(2)}</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text>Subtotale:</Text>
                <Text>{formatCurrency(customTotalPrice)}</Text>
              </View>

              {unmountPrice > 0 && (
                <View style={styles.summaryRow}>
                  <Text>Smontaggio:</Text>
                  <Text>{formatCurrency(unmountPrice)}</Text>
                </View>
              )}

              {aditionalCost > 0 && (
                <View style={styles.summaryRow}>
                  <Text>Osservazione*:</Text>
                  <Text>{formatCurrency(aditionalCost)}</Text>
                </View>
              )}

              <View style={styles.totalRow}>
                <Text>Totale:</Text>
                <Text>{formatCurrency(finalTotal)}</Text>
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

const TablePDFGenerator: React.FC<ListPDFProps> = ({
  carParts,
  costumer,
  pricePerWorkingHour,
  unmountPrice,
  car,
  aditionalCost,
  aditionalCostDescription,
  date,
  insuranceHours = 0,
  costumerPrice = 0
}) => {
  const { chassisNumber } = car;

  let chassisNumberValue = chassisNumber.length < 2 ? "assente" : chassisNumber;
  const fileName = `${removeWhiteSpaces(costumer)}-${chassisNumberValue}-${date.toLocaleDateString("pt-BR")}-tabella.pdf`;

  return (
    <>
      <PDFDownloadLink
        document={
          <TablePDFDocument
            carParts={carParts}
            costumer={costumer}
            pricePerWorkingHour={pricePerWorkingHour}
            unmountPrice={unmountPrice}
            car={car}
            aditionalCost={aditionalCost}
            aditionalCostDescription={aditionalCostDescription}
            date={date}
            insuranceHours={insuranceHours}
            costumerPrice={costumerPrice}
          />
        }
        fileName={fileName}
      >
        {({ loading }: { loading: boolean }) => (
          <Button fullWidth variant="contained" sx={{ mb: 1, mt: 5 }} disabled={loading}>
            {loading ? "Gerando PDF..." : "GERAR PDF DA TABELA"}
          </Button>
        )}
      </PDFDownloadLink>
    </>
  );
};

export default TablePDFGenerator;
